// src/pages/HRAdmin/Position/PositionList.js
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import BadgeIcon from '@mui/icons-material/Badge';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import NotesIcon from '@mui/icons-material/Notes';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';

const normalizeList = (payload) => {
  const list = payload?.data?.data ?? payload?.data ?? [];
  return Array.isArray(list) ? list : [];
};

const textOf = (value) => (value == null ? '' : String(value));

function StatCard({ icon, label, value, tone = 'primary' }) {
  const theme = useTheme();
  const color =
    tone === 'success'
      ? theme.palette.success.main
      : tone === 'info'
        ? theme.palette.info.main
        : theme.palette.primary.main;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.25,
        borderRadius: '8px',
        border: `1px solid ${theme.hr?.line?.soft || alpha(theme.palette.divider, 0.16)}`,
        background: theme.hr?.surface?.glass || alpha(theme.palette.background.paper, 0.88),
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="center">
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: '8px',
            display: 'grid',
            placeItems: 'center',
            color,
            backgroundColor: alpha(color, 0.11),
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {label}
          </Typography>
          <Typography variant="h6" sx={{ lineHeight: 1.15 }}>
            {value}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
}

function PositionList() {
  const navigate = useNavigate();
  const theme = useTheme();
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchPositions();
  }, []);

  const fetchPositions = () => {
    setLoading(true);
    setError('');
    api
      .get('/positions')
      .then((res) => setPositions(normalizeList(res)))
      .catch((err) => {
        console.error('Failed to fetch positions:', err);
        setError('Could not load positions.');
      })
      .finally(() => setLoading(false));
  };

  const handleAddNew = () => {
    navigate('/admin/positions/new');
  };

  const handleEdit = (id) => {
    navigate(`/admin/positions/edit/${id}`);
  };

  const handleDelete = (id) => {
    if (!window.confirm('Are you sure you want to delete this position?')) return;
    api
      .delete(`/positions/${id}`)
      .then(() => fetchPositions())
      .catch((err) => {
        console.error('Failed to delete position:', err);
        setError('Could not delete this position.');
      });
  };

  const stats = useMemo(() => {
    const withCode = positions.filter((position) => textOf(position.code).trim()).length;
    const withDescription = positions.filter((position) => textOf(position.description).trim()).length;

    return {
      total: positions.length,
      withCode,
      missingCode: Math.max(positions.length - withCode, 0),
      withDescription,
    };
  }, [positions]);

  const filteredPositions = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return positions;

    return positions.filter((position) =>
      [position.name, position.code, position.description]
        .map(textOf)
        .join(' ')
        .toLowerCase()
        .includes(query)
    );
  }, [positions, searchTerm]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        p: { xs: 1.25, md: 2 },
        background:
          theme.hr?.gradient?.page ||
          `linear-gradient(180deg, ${theme.palette.background.default}, ${alpha(
            theme.palette.primary.main,
            0.04
          )})`,
        '& .MuiButton-root, & .MuiOutlinedInput-root, & .MuiChip-root': {
          borderRadius: '8px',
        },
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.25}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'center' }}
        sx={{ mb: 1.5 }}
      >
        <Box>
          <Typography variant="overline" color="text.secondary">
            Organization setup
          </Typography>
          <Typography variant="h4" sx={{ mt: -0.5 }}>
            Positions
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Maintain job titles, codes, and descriptions used across employee records.
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-end', sm: 'initial' }}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchPositions} disabled={loading}>
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddNew}
            sx={{ boxShadow: 'none' }}
          >
            Add Position
          </Button>
        </Stack>
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' },
          gap: 1.5,
          mb: 1.5,
        }}
      >
        <StatCard icon={<BadgeIcon fontSize="small" />} label="Positions" value={stats.total} />
        <StatCard icon={<BadgeIcon fontSize="small" />} label="With code" value={stats.withCode} tone="success" />
        <StatCard icon={<BadgeIcon fontSize="small" />} label="Missing code" value={stats.missingCode} tone="info" />
        <StatCard
          icon={<NotesIcon fontSize="small" />}
          label="With description"
          value={stats.withDescription}
          tone="info"
        />
      </Box>

      {error ? (
        <Alert severity="error" sx={{ mb: 1.5, borderRadius: '8px' }} onClose={() => setError('')}>
          {error}
        </Alert>
      ) : null}

      <Paper
        elevation={0}
        sx={{
          p: 1.25,
          mb: 1.5,
          borderRadius: '8px',
          border: `1px solid ${theme.hr?.line?.soft || alpha(theme.palette.divider, 0.16)}`,
          background: theme.hr?.surface?.glass || alpha(theme.palette.background.paper, 0.88),
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
          <TextField
            size="small"
            fullWidth
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Search position, code, or description"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Chip
            label={`${filteredPositions.length} shown`}
            color="primary"
            variant="outlined"
            sx={{ alignSelf: { xs: 'flex-start', md: 'center' } }}
          />
        </Stack>
      </Paper>

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          borderRadius: '8px',
          border: `1px solid ${theme.hr?.line?.soft || alpha(theme.palette.divider, 0.16)}`,
          background: theme.hr?.surface?.paper || theme.palette.background.paper,
          overflow: 'hidden',
        }}
      >
        {loading ? (
          <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 220 }}>
            <CircularProgress size={28} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Loading positions...
            </Typography>
          </Stack>
        ) : (
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>Position</TableCell>
                <TableCell>Code</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredPositions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Stack alignItems="center" justifyContent="center" sx={{ py: 5 }}>
                      <Typography variant="subtitle1">No positions found</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Try another search or add a new position.
                      </Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              ) : (
                filteredPositions.map((pos) => (
                  <TableRow key={pos.id} hover sx={{ '&:last-child td': { borderBottom: 0 } }}>
                    <TableCell>
                      <Typography variant="subtitle2">{pos.name || '-'}</Typography>
                    </TableCell>
                    <TableCell>
                      {pos.code ? <Chip size="small" label={pos.code} variant="outlined" /> : '-'}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 620 }}>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {pos.description || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.75} justifyContent="flex-end">
                        <Button
                          variant="outlined"
                          color="primary"
                          size="small"
                          startIcon={<EditIcon />}
                          onClick={() => handleEdit(pos.id)}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          startIcon={<DeleteOutlineIcon />}
                          onClick={() => handleDelete(pos.id)}
                        >
                          Delete
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>
    </Box>
  );
}

export default PositionList;
